import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { trackCustomEvent } from "@/utils/analyticsTracker";

const GetPremium = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoading, session, isSubscribed } = useAuth();

  // Track canceled Stripe checkouts before redirecting
  useEffect(() => {
    if (searchParams.get("canceled") === "true") {
      trackCustomEvent("stripe_checkout_cancelled", {
        extraData: { sessionId: searchParams.get("session_id") },
      });

      // Clean up the URL so it doesn't trigger again
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  useEffect(() => {
    // Log detailed authentication state when component mounts
    console.log("GetPremium - Initial Auth State:", {
      isUserAuthenticated: !!user,
      userId: user?.id || "No user ID",
      userEmail: user?.email || "No email",
      isLoading,
      hasSession: !!session,
      isSubscribed,
      timestamp: new Date().toISOString(),
      routePath: "/get-premium",
    });

    // If user is already subscribed, redirect to /learn
    if (!isLoading && isSubscribed) {
      console.log("User is already subscribed, redirecting to /learn");
      navigate("/learn");
      return;
    }

    // Track the premium intent
    const trackPremiumIntent = async () => {
      if (user?.email) {
        try {
          // Send SMS notification for premium interest
          await supabase.functions.invoke("send-sms-notification", {
            body: {
              name:
                user.user_metadata?.full_name ||
                user.user_metadata?.name ||
                null,
              email: user.email,
              notificationType: "payment_initiated",
            },
          });

          console.log("Premium intent SMS notification sent");
        } catch (error) {
          console.error("Error sending premium intent notification:", error);
          // Don't block the redirect if notification fails
        }
      }

      // Log before redirect
      console.log("GetPremium - About to redirect to pricing page", {
        authStatus: isLoading
          ? "loading"
          : user
            ? "authenticated"
            : "unauthenticated",
        timestamp: new Date().toISOString(),
      });

      // Redirect to the pricing page
      navigate("/pricing");
    };

    // Only track and redirect if authentication state is resolved and user is not already subscribed
    if (!isLoading && !isSubscribed) {
      trackPremiumIntent();
    }
  }, [navigate, user, isLoading, session, isSubscribed]);

  // Log right before rendering
  console.log("GetPremium - Rendering with auth state:", {
    authStatus: isLoading
      ? "loading"
      : user
        ? "authenticated"
        : "unauthenticated",
    isSubscribed,
    timestamp: new Date().toISOString(),
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow py-16 md:py-24">
        <div className="container">
          <div className="flex justify-center">
            <div className="animate-pulse text-center">
              <p>Redirecting to pricing page...</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GetPremium;
